import React, { useEffect, useState } from 'react';
import { setBreadcrumbs } from 'works/legacy/lib/client-store';
import { FullScreenLoadingBeat, PageSteps } from '@common/components';
import { RcFile } from 'antd/lib/upload';
import { CSVError } from 'works/legacy/lib/csv-error';
import { WorkerUploadFileUploadStep } from '@scheduling/components';
import { useMutation, useQuery, useNavigate } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  ImportWorker,
  ImportWorkerProgressQuery,
  ImportWorkerProgressResponse,
  ImportWorkerResponse,
  ImportWorkers,
  ImportWorkerVariables,
  ValidateWorkersImport,
  ValidateWorkersImportResponse,
  ValidateWorkersImportVariables,
} from '@scheduling/graphql/mutations/workers-import';
import { WorkerUploadReview } from '@scheduling/components/worker-upload-steps/worker-upload-review';
import { Button } from 'antd';
import { WorkerUploadConfirmModal } from '@scheduling/components/worker-upload-steps/worker-upload-confirm-modal';

export function WorkerUploadSteps() {
  useEffect(() => {
    setBreadcrumbs([
      {
        name: 'Team',
        path: '/works/team/on_demand',
      },
      {
        name: 'Upload Staff',
        path: '/works/team/on_demand/upload',
      },
    ]);
  }, []);

  const navigate = useNavigate();
  const [csvFile, setCsvFile] = useState<RcFile | null>(null);
  const [errors, setErrors] = useState<CSVError[]>([]);
  const [importWorkers, setImportWorkers] = useState<ImportWorker[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [workerImportId, setWorkerImportId] = useState<string | null>(null);
  const [progressPending, setProgressPending] = useState(true);
  const [inviteWorkers, setInviteWorkers] = useState<boolean>(false);

  const errorMessage = () =>
    createMessage(
      'error',
      'Something went wrong, please contact support@workshealth.com for assistance.'
    );

  const [validateFile, { loading }] = useMutation<
    ValidateWorkersImportResponse,
    ValidateWorkersImportVariables
  >(ValidateWorkersImport, {
    onCompleted: ({ validateWorkersImport: { errors, workers } }) => {
      if (errors.length > 0) {
        setErrors(errors);
      } else {
        setImportWorkers(workers);
      }
    },
    onError: errorMessage,
  });

  const handleFileUpload = (file: RcFile) => {
    setImportWorkers([]);
    setErrors([]);
    setCsvFile(file);

    validateFile({
      variables: {
        input: {
          file,
        },
      },
    });

    return false;
  };

  const {
    data: progressData,
    startPolling: startPoolingProgress,
    stopPolling: stopPoolingProgress,
  } = useQuery<ImportWorkerProgressResponse>(ImportWorkerProgressQuery, {
    skip: !workerImportId,
    variables: { id: workerImportId },
  });

  useEffect(() => {
    const isPending = progressData?.workerImport?.pending !== false;
    setProgressPending(isPending);

    if (!isPending) {
      stopPoolingProgress();

      const state = progressData?.workerImport?.state ?? 'failed';

      if (state === 'failed') {
        errorMessage();
      } else {
        createMessage('success', 'Staff imported successfully.');
      }

      navigate('/works/team/on_demand');
    }
  }, [navigate, progressData, stopPoolingProgress]);

  const [save, { loading: saveLoading }] = useMutation<
    ImportWorkerResponse,
    ImportWorkerVariables
  >(ImportWorkers, {
    onCompleted({ createWorkerImport }: ImportWorkerResponse) {
      if (createWorkerImport.errors) {
        errorMessage();
        return;
      }

      const newWorkerImportId = createWorkerImport.id;
      setWorkerImportId(newWorkerImportId);

      if (newWorkerImportId) {
        startPoolingProgress(1000);
      }
    },
    onError: errorMessage,
  });

  const handleSave = () => {
    if (!csvFile) {
      errorMessage();
      return;
    }

    save({
      variables: {
        input: {
          file: csvFile,
          inviteUponCreation: inviteWorkers,
        },
      },
    });
  };

  return (
    <>
      <WorkerUploadConfirmModal
        open={showConfirmModal}
        workersCount={importWorkers.length}
        onConfirm={handleSave}
        confirmLoading={saveLoading}
        onCancel={() => setShowConfirmModal(false)}
        setInviteWorkers={setInviteWorkers}
      />
      <FullScreenLoadingBeat
        open={!!workerImportId && progressPending}
        description="Updates in progress, please do not refresh or close the page."
      />
      <PageSteps
        steps={[
          {
            title: 'Upload File',
            page: (
              <WorkerUploadFileUploadStep
                handleFileUpload={handleFileUpload}
                csvFile={csvFile}
              />
            ),
            forwardButtonDisabled: !importWorkers.length && !errors.length,
            forwardButtonLoading: loading,
          },
          {
            title: 'Review Import',
            page: (
              <WorkerUploadReview
                importWorkers={importWorkers}
                errors={errors}
              />
            ),
            customForwardAction() {
              if (errors.length) return <></>;

              return (
                <>
                  <Button
                    onClick={() => setShowConfirmModal(true)}
                    size="large"
                    type="primary"
                  >
                    Upload Staff
                  </Button>
                </>
              );
            },
          },
        ]}
        onConfirm={() => {}}
      />
    </>
  );
}
